import type { BackendExperiment } from '@atlassian/search-experiment/types';

import { type FireAnalyticsEvent } from '../../../common/types';
import { ALL_PRODUCTS_STRING, type QueryState } from '../../constants/analytics';
import { is3pProductKeyForAnalytics } from '../../constants/is3p-product-key-for-analytics';
import type { ProductKeys } from '../../constants/product-keys';

import {
	type CommonAttributes,
	type NonPrivacySafeAttributes,
	type ResultShown,
	type SearchAndResultAttributes,
	type SocialSignalAttributes,
} from './types';

type AdvancedResultsShownAnalyticsProps = {
	queryState: QueryState;
	numResults: number | undefined;
	shownCount: number;
	are3pResultsShown: boolean;
	commonAttributes: CommonAttributes;
	nonPrivacySafeAttributes: NonPrivacySafeAttributes;
	searchAndResult: SearchAndResultAttributes;
	resultsShown: ResultShown[];
	socialSignalsAttributes: SocialSignalAttributes;
	requestedBackendExperiments: { backendExperiments: BackendExperiment };
	referrer: string | undefined;
	/** Whether the query used to search for these results was paginated.
	 * i.e. will be false for the first page of results, and true for subsequent pages.
	 */
	isPaginatedQuery: boolean;
	isBulkConnectBannerShown: boolean;
	productsWithTimeout: ProductKeys[] | undefined;
	intentDetectionTriggered?: boolean;
	queryClassification?: string;
	modelId?: string;
	cohort?: string;
	patternType?: string;
	wordCount?: number;
	/** How many sites the search covered. Undefined when the search is not site-scoped. */
	selectedSitesCount?: number;
	/** How many sites are available to the user. Undefined until site metadata has loaded. */
	availableSitesCount?: number;
};

export const onAdvancedSearchResultsShown = ({
	queryState,
	numResults,
	shownCount,
	are3pResultsShown,
	commonAttributes,
	nonPrivacySafeAttributes,
	searchAndResult,
	resultsShown,
	socialSignalsAttributes,
	requestedBackendExperiments,
	referrer,
	isPaginatedQuery,
	isBulkConnectBannerShown,
	productsWithTimeout,
	intentDetectionTriggered,
	queryClassification,
	modelId,
	cohort,
	patternType,
	wordCount,
	selectedSitesCount,
	availableSitesCount,
}: AdvancedResultsShownAnalyticsProps): FireAnalyticsEvent => {
	// Build connectorInfo array for unified 3P instrumentation
	// Only include entries for 3P results that have connectorInfo populated
	const connectorInfo = resultsShown
		.map((result) => result.connectorInfo)
		.filter((info): info is NonNullable<typeof info> => info !== undefined);

	return {
		eventType: 'track',
		action: 'shown',
		actionSubject: 'advancedSearchResults',
		attributes: {
			additionalAttributes: {
				queryState,
				numResults,
				numberOfResultsShown: shownCount,
				noResultsFetched: numResults === 0,
				numberOfDocs: numResults,
				are3pResultsShown,
				searchAndResult,
				resultsShown,
				// Add connectorInfo array for unified 3P instrumentation
				connectorInfo: connectorInfo.length > 0 ? connectorInfo : undefined,
				is3PFilter:
					commonAttributes.productSelected !== ALL_PRODUCTS_STRING &&
					is3pProductKeyForAnalytics(commonAttributes.productSelected),
				isPaginatedQuery,
				isBulkConnectBannerShown,
				selectedSitesCount,
				availableSitesCount,
				referrer,
				...socialSignalsAttributes,
				backendExperiments: {
					experimentLayers: requestedBackendExperiments.backendExperiments?.experimentLayers?.map(
						({ analyticsDefinitions, ...layer }) => {
							return {
								...layer,
								definitions: analyticsDefinitions,
							};
						},
					),
				},
				productsWithTimeout,
				...(intentDetectionTriggered !== undefined ? { intentDetectionTriggered } : {}),
				queryClassification,
				modelId,
				cohort,
				patternType,
				wordCount,
			},
			commonAttributes,
		},
		nonPrivacySafeAttributes,
	};
};
